import type {
  CredentialStatus,
  ProviderCredentialResponse,
  ProviderType,
} from "@/api";
import type { BadgeTone } from "@/components/ui/badge";

/** Bahasa-friendly labels per provider. The display label keeps the
 *  vendor's preferred casing — "OpenAI" not "Openai" — so the
 *  procurement reader sees the BYOK list in the same form they see
 *  it on the vendor's own dashboard. */
export const PROVIDER_TYPE_LABEL: Record<ProviderType, string> = {
  openai: "OpenAI",
  anthropic: "Anthropic",
  bedrock: "AWS Bedrock",
};

export const CREDENTIAL_STATUS_LABEL: Record<CredentialStatus, string> = {
  active: "Aktif",
  disabled: "Dinonaktifkan",
  revoked: "Dicabut",
};

export const CREDENTIAL_STATUS_TONE: Record<CredentialStatus, BadgeTone> = {
  active: "success",
  disabled: "neutral",
  revoked: "danger",
};

export function formatProviderLabel(type: string): string {
  return PROVIDER_TYPE_LABEL[type as ProviderType] ?? type;
}

export function formatCredentialStatus(status: string): string {
  return CREDENTIAL_STATUS_LABEL[status as CredentialStatus] ?? status;
}

/** Render a credential as ``sk-…-WXYZ`` so the table stays scannable
 *  without ever surfacing the plaintext. The prefix follows the
 *  vendor's own convention (``sk-`` for OpenAI / Anthropic). */
export function formatMaskedKey(
  providerType: string,
  last4: string
): string {
  const prefix =
    providerType === "openai" || providerType === "anthropic" ? "sk" : "key";
  return `${prefix}-…-${last4}`;
}

/** Collapse the backend's "empty string = no rotation" convention
 *  to ``string | undefined`` so callers can simply check
 *  ``if (extractRotatedKey(response))`` to decide whether to open
 *  the show-once dialog. */
export function extractRotatedKey(
  credential: ProviderCredentialResponse
): string | undefined {
  const plaintext = credential.api_key_plaintext;
  if (!plaintext || plaintext.trim() === "") {
    return undefined;
  }
  return plaintext;
}
